import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Edit as EditIcon, Search as SearchIcon, Business as BusinessIcon } from '@mui/icons-material';
import { useAuth } from '@hooks/useAuth';
import { getAllOrganizations } from '@services/organizationService';

const AdminOrganizationsPage = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (userData?.userType !== 'platform-admin') {
      navigate('/dashboard');
      return;
    }
    getAllOrganizations()
      .then(setOrgs)
      .catch(() => setError('Failed to load organizations.'))
      .finally(() => setLoading(false));
  }, [userData, navigate]);

  const filtered = orgs.filter(o =>
    !search || o.organizationName?.toLowerCase().includes(search.toLowerCase())
  );

  const statusColor = (status) => {
    if (status === 'active') return 'success';
    if (status === 'pending') return 'warning';
    return 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <BusinessIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h4" fontWeight="bold">Organizations</Typography>
          <Typography variant="body2" color="text.secondary">
            {orgs.length} organization{orgs.length !== 1 ? 's' : ''} registered
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <TextField
        placeholder="Search organizations…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        size="small"
        sx={{ mb: 2, width: 320 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
        }}
      />

      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Contact Email</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="right"><strong>Edit</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No organizations found.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map(org => (
                <TableRow key={org.organizationId} hover>
                  <TableCell>{org.organizationName}</TableCell>
                  <TableCell>{org.contactEmail || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={org.status || 'unknown'}
                      color={statusColor(org.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit organization settings">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/org-settings/${org.organizationId}`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default AdminOrganizationsPage;
